/**
 * 철수해 돌아온 뒤, 편성 화면 맨 위에 **무엇을 받았는지** 적는 줄.
 *
 * 결과 화면은 철수하기 **전에** "월급 +1,500냥"을 예고할 뿐이고, 철수를 누르면 곧장 편성 화면으로 넘어가 군자금 숫자만
 * 바뀌어 있었다. 플레이한 사람: "내가 월급은 받았는지 알 길이 없는 거 같아." 받은 것(전과·노획·월급)을 한 줄로 적고,
 * 월급이 없거나 덜 나왔으면 까닭(이겨야 하는 깊이)을 같이 적는다. 닫기 전까지 남는다 — 토스트처럼 사라지면 못 본다.
 */
import { button, el } from './dom.ts';

/** 철수로 확정된 것. `retreat`을 부르기 **전에** 떠 둔다(부른 뒤에는 출정의 값이 비워진다). */
export interface Homecoming {
  /** 확정된 전과. */
  readonly merit: number;
  /** 들어온 노획(냥). */
  readonly loot: number;
  /** 받은 월급(냥). */
  readonly salary: number;
  /** 제 관등의 온 월급(냥). 0이면 월급이 없는 관등이다(편장). */
  readonly fullSalary: number;
  /** 온 월급을 받으려면 그 출정에서 이겨야 했던 깊이. */
  readonly needDepth: number;
  /** 얼마라도 받으려면 이겨야 했던 가장 얕은 깊이. */
  readonly floorDepth: number;
  /** 월급을 받은(받을) 관등의 이름. */
  readonly rank: string;
  /** 도적 토벌이었는가 — 월급이 아예 없는 출정이라 월급 이야기를 하지 않는다. */
  readonly raid: boolean;
  /**
   * 그 밖에 철수와 함께 들어온 돈(냥): 철수하면 세계가 한 차례 돌아(`realmTick`) 영토의 수입·방어 상금·시즌 보상이
   * 들어올 수 있다. 철수 앞뒤의 군자금 차이에서 노획과 월급을 뺀 값이다(`main.ts`). 없으면 0.
   */
  readonly other?: number;
}

/** 줄의 조각들: [글, 좋은 소식인가]. 화면과 시험이 같이 쓴다. */
export function homecomingParts(h: Homecoming): [string, boolean][] {
  const out: [string, boolean][] = [];
  if (h.merit > 0) out.push([`전과 ${h.merit.toLocaleString()} 확정`, true]);
  if (h.loot > 0) out.push([`노획 +${h.loot.toLocaleString()}냥`, true]);
  if (!h.raid && h.fullSalary > 0) {
    if (h.salary >= h.fullSalary) out.push([`월급 +${h.salary.toLocaleString()}냥(${h.rank})`, true]);
    else if (h.salary > 0) out.push([`월급 +${h.salary.toLocaleString()}냥 — 깊이 ${h.needDepth} 이상에서 이겼으면 ${h.fullSalary.toLocaleString()}냥`, true]);
    else out.push([`월급 없음 — 깊이 ${h.floorDepth} 이상에서 이긴 판이 있어야 나온다`, false]);
  }
  // 영토에서 들어온 돈. 처음에는 전과·노획·월급만 세어, 영토 수입 20냥을 받고도 "받은 것이 없다"고 적었다(리뷰가 재현했다).
  if ((h.other ?? 0) > 0) out.push([`영토 수입 +${(h.other ?? 0).toLocaleString()}냥`, true]);
  // 아무것도 없어도(한 판도 못 이기고 물러났다) 돌아왔다는 것은 적는다.
  if (out.length === 0) out.push(['이번 출정에서 얻은 것이 없다', false]);
  return out;
}

/** 편성 화면의 머리줄 밑에 얹는 줄. 닫으면 사라진다(`onClose`로 쥔 쪽도 비운다). */
export function homecomingBar(h: Homecoming, onClose: () => void = () => {}): HTMLElement {
  const bar = el(
    'div',
    { class: 'home-bar', role: 'status' },
    el('b', {}, '돌아왔다'),
    el('span', { class: 'home-parts' }, ...homecomingParts(h).map(([t, good]) => el('span', { class: good ? 'good' : 'dim' }, t))),
  );
  const close = button('닫기', () => {
    onClose();
    // 닫으면 초점을 쥔 단추가 사라져 초점이 문서로 떨어진다(리뷰·QA) — 줄의 바로 앞, 머리줄의 마지막 단추로 넘긴다.
    const prev = bar.previousElementSibling?.querySelector<HTMLElement>('button:last-of-type');
    bar.remove();
    prev?.focus({ preventScroll: true });
  }, 'mini');
  close.setAttribute('aria-label', '돌아온 소식 닫기');
  bar.append(close);
  return bar;
}
